import { useState, useRef, useEffect, type MouseEvent } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  type Placement,
} from '@floating-ui/react';
import { FileDown, Check, Plus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCartActions } from '../../hooks/useCartActions';
import { useTranslation } from '../../i18n';

interface CartPickerProps {
  productId: number;
  size?: 'sm' | 'md';
  align?: 'left' | 'right';
  direction?: 'up' | 'down';
}

export default function CartPicker({
  productId,
  size = 'sm',
  align = 'left',
  direction = 'up',
}: CartPickerProps) {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const {
    carts,
    createCart,
    addToSpecificCart,
    removeFromSpecificCart,
  } = useCartActions(isLoggedIn);

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCartName, setNewCartName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const placement: Placement = `${direction === 'down' ? 'bottom' : 'top'}-${
    align === 'right' ? 'end' : 'start'
  }`;

  const {
    refs: floatingRefs,
    floatingStyles,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const inAnyCart = carts.some((c) => (c.products ?? []).some((p) => p.id === productId));
  const iconSize = size === 'md' ? 'w-5 h-5' : 'w-4 h-4';

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      const clickedTrigger = ref.current?.contains(target);
      const clickedMenu = floatingRefs.floating.current?.contains(target);
      if (!clickedTrigger && !clickedMenu) {
        setOpen(false);
        setCreating(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, floatingRefs]);

  const handleButtonClick = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleCartSelect = (cartId: number) => {
    const cartProducts = carts.find((c) => c.id === cartId)?.products ?? [];
    const alreadyInThisCart = cartProducts.some((p) => p.id === productId);

    if (alreadyInThisCart) {
      removeFromSpecificCart(cartId, productId);
    } else {
      addToSpecificCart(cartId, productId);
    }
  };

  const handleRemoveFromAll = () => {
    for (const cart of carts) {
      const inThisCart = (cart.products ?? []).some((p) => p.id === productId);
      if (inThisCart) {
        removeFromSpecificCart(cart.id, productId);
      }
    }
    setOpen(false);
  };

  const handleCreateCart = () => {
    const trimmed = newCartName.trim();
    if (trimmed) {
      createCart(trimmed);
      setNewCartName('');
      setCreating(false);
    }
  };

  const buttonLabel = inAnyCart ? t.actions.inCartLabel : t.actions.cartLabel;

  return (
    <div className="relative" ref={ref}>
      {size === 'md' ? (
        <button
          ref={floatingRefs.setReference}
          type="button"
          title={t.actions.cartLabel}
          className={`flex flex-col items-center gap-1 rounded-lg px-3 py-2.5 text-xs font-medium transition-all duration-200 cursor-pointer w-full ${
            inAnyCart
              ? 'bg-vivatis-50 text-vivatis-600 dark:bg-vivatis-950/40 dark:text-vivatis-400'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
          }`}
          onClick={handleButtonClick}
        >
          <FileDown className={`${iconSize} transition-colors ${inAnyCart ? 'text-vivatis-500' : ''}`} />
          <span className="leading-none">{buttonLabel}</span>
        </button>
      ) : (
        <button
          // eslint-disable-next-line react-hooks/refs
          ref={floatingRefs.setReference}
          type="button"
          title={t.actions.cartLabel}
          className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          onClick={handleButtonClick}
        >
          <FileDown
            className={`${iconSize} transition-colors ${
              inAnyCart ? 'text-vivatis-500' : 'text-gray-400 dark:text-gray-500'
            }`}
          />
        </button>
      )}

      {open && (
        <FloatingPortal>
          <div
            // eslint-disable-next-line react-hooks/refs
            ref={floatingRefs.setFloating}
            style={floatingStyles}
            className="z-[60] w-52 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1"
          >
            {inAnyCart && (
              <>
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFromAll();
                  }}
                >
                  {t.actions.removeFromAllCarts}
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              </>
            )}
            <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 py-1">
              {t.cart.title}
            </p>
            {carts.map((cart) => {
              const productInThisCart = (cart.products ?? []).some((p) => p.id === productId);
              return (
                <button
                  key={cart.id}
                  type="button"
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors cursor-pointer ${
                    productInThisCart
                      ? 'bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/20 dark:text-vivatis-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCartSelect(cart.id);
                  }}
                >
                  {productInThisCart ? (
                    <Check className="w-3.5 h-3.5 shrink-0 text-vivatis-500" />
                  ) : (
                    <span className="w-3.5 shrink-0" />
                  )}
                  <span className="truncate flex-1">{cart.name}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{(cart.products ?? []).length}</span>
                </button>
              );
            })}

            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            {creating ? (
              <form
                className="px-3 py-1.5 flex gap-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateCart();
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={newCartName}
                  onChange={(e) => setNewCartName(e.target.value)}
                  placeholder="Document name..."
                  className="flex-1 min-w-0 text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-vivatis-500"
                  autoFocus
                />
                <button
                  type="submit"
                  className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-vivatis-600 text-white hover:bg-vivatis-700"
                >
                  Add
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setCreating(true);
                }}
              >
                <Plus className="w-3.5 h-3.5" />
                <span>{t.cart.newDocument}</span>
              </button>
            )}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
